"use client";

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { FiPlus, FiTrash2, FiX } from "react-icons/fi";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { Button, Input, Label, Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import ColorPicker from "@/components/ui/color-picker";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { sonnerToast, toast } from "@/lib/use-toast";
import { CarePlanTagDetail } from "@/types/care-plan-template";
import { DEFAULT_TAG_COLOR } from "./constants";
import { carePlanTagQueryOptions } from "@/api/provider/settings/care-plan-tag-query";
import { useCreateCarePlanTagMutation, useDeleteCarePlanTagMutation } from "@/query/settings/care-plan-tag";
interface CarePlanTagPickerProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  onSavingChange?: (isSaving: boolean) => void;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
}
export function CarePlanTagPicker({
  className,
  selectedTagIds,
  onChange,
  onSavingChange,
  hasError,
  errorMessage
}: CarePlanTagPickerProps) {
  const {
    data: tags
  } = useQuery(carePlanTagQueryOptions.list());
  const createMutation = useCreateCarePlanTagMutation();
  const deleteMutation = useDeleteCarePlanTagMutation();
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_TAG_COLOR);
  const [deleteTag, setDeleteTag] = useState<CarePlanTagDetail | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const selectedTagIdSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);
  const toggleTag = useCallback((tagId: string) => {
    if (selectedTagIdSet.has(tagId)) {
      onChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  }, [selectedTagIds, selectedTagIdSet, onChange]);
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    onSavingChange?.(true);
    const name = newTagName.trim();
    const color = newTagColor;
    setNewTagName("");
    setIsCreating(false);
    try {
      const result = await createMutation.mutateAsync({
        name,
        color
      });
      if (result) {
        onChange([...selectedTagIds, result.id]);
      }
    } catch {
      toast({
        title: "Failed to create tag",
        variant: "error"
      });
    } finally {
      onSavingChange?.(false);
    }
  };
  const handleDeleteTag = () => {
    if (!deleteTag) return;
    const tagId_0 = deleteTag.id;
    sonnerToast.promise(deleteMutation.mutateAsync(tagId_0), {
      loading: "Deleting tag...",
      success: "Tag deleted",
      error: async err => {
        return await parseError(err, "Failed to delete tag");
      }
    });
  };
  const allTags = tags ?? [];
  const {
    activeTags,
    selectedTags
  } = useMemo(() => {
    const active: typeof allTags = [];
    const selected: typeof allTags = [];
    for (const tag of allTags) {
      if (selectedTagIdSet.has(tag.id)) {
        selected.push(tag);
      } else {
        active.push(tag);
      }
    }
    return {
      activeTags: active,
      selectedTags: selected
    };
  }, [allTags, selectedTagIdSet]);
  return <div className={cx("space-y-2", className)}>
      <Label className="text-caption text-foreground-secondary">Tags</Label>

      {/* Selected tags */}

      <div className="flex flex-wrap gap-1.5 !mt-0">
        {selectedTags.length > 0 ? selectedTags.map(tag_0 => <button key={tag_0.id} type="button" onClick={() => toggleTag(tag_0.id)} className={cx("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-caption-strong", "transition-colors hover:opacity-80")} style={{
        backgroundColor: `${tag_0.color ?? DEFAULT_TAG_COLOR}20`,
        color: tag_0.color ?? DEFAULT_TAG_COLOR
      }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{
          backgroundColor: tag_0.color ?? DEFAULT_TAG_COLOR
        }} />
              {tag_0.name}
              <FiX className="h-3 w-3" />
            </button>) : <p className="text-caption rounded-sm border border-dashed px-2.5 py-0.5 text-foreground-tertiary">
            No tags selected
          </p>}
      </div>

      <div className="flex items-center gap-2 flex-wrap gap-y-1">
        {/* Available tags */}
        {activeTags.length > 0 && <div className="flex flex-wrap gap-1.5">
            {activeTags.map(tag_1 => <div key={tag_1.id} className="group inline-flex items-center gap-0.5">
                <button type="button" onClick={() => toggleTag(tag_1.id)} className={cx("inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-caption", "text-foreground-secondary transition-colors hover:bg-hover")}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{
              backgroundColor: tag_1.color ?? DEFAULT_TAG_COLOR
            }} />
                  {tag_1.name}
                </button>
                <button type="button" onClick={() => setDeleteTag(tag_1)} className="hidden group-hover:inline-flex items-center justify-center h-4 w-4 rounded-full text-foreground-muted hover:text-status-error-text transition-colors" title="Delete tag">
                  <FiTrash2 className="h-2.5 w-2.5" />
                </button>
              </div>)}
          </div>}
        {/* Create new tag inline */}
        {isCreating ? <div className="flex items-center gap-2 pt-1">
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="shrink-0" aria-label="Pick color">
                  <span className="block h-4 w-4 rounded-full border border-border" style={{
                backgroundColor: newTagColor
              }} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-2">
                <ColorPicker color={newTagColor} onChange={hex => setNewTagColor(hex)} closeOnSelect />
              </PopoverContent>
            </Popover>
            <Input ref={nameRef} value={newTagName} onChange={e => setNewTagName(e.target.value)} placeholder="Tag name" className="h-7 flex-1 text-caption" autoFocus onKeyDown={e_0 => {
          if (e_0.key === "Enter") {
            e_0.preventDefault();
            handleCreateTag();
          }
          if (e_0.key === "Escape") {
            setIsCreating(false);
            setNewTagName("");
          }
        }} />
            <Button type="button" size="sm" onClick={handleCreateTag} disabled={!newTagName.trim() || createMutation.isPending} className="h-7 text-caption">
              Add
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => {
          setIsCreating(false);
          setNewTagName("");
        }} className="h-7 text-caption">
              Cancel
            </Button>
          </div> : <Button onClick={() => setIsCreating(true)} variant="ghost" size="sm" icon={<FiPlus className="h-3 w-3" />} className="flex !p-0 hover:!bg-transparent text-foreground-muted hover:text-foreground">
            New Tag
          </Button>}
      </div>

      {hasError && errorMessage && <p className="text-caption text-status-error-text">{errorMessage}</p>}

      <DeleteConfirmationModal isOpen={!!deleteTag} setIsOpen={open => !open && setDeleteTag(null)} onConfirm={handleDeleteTag} title="Delete Tag" subtitle={`Are you sure you want to delete "${deleteTag?.name}"? This action cannot be undone.`} confirmText="Delete" isLoading={deleteMutation.isPending} />
    </div>;
}